import { SITE } from "@/lib/siteConfig";

function LegalLayout({ eyebrow, title, lastUpdated, children }) {
  return (
    <div data-testid="legal-page">
      <section className="bg-[#F4F7FC] py-20 md:py-24 px-6 md:px-10">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-[#C9A84C] mb-5">
            <span className="h-[1px] w-8 bg-[#C9A84C]" />
            {eyebrow}
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#1E2761] tracking-tight leading-tight">
            {title}
          </h1>
          {lastUpdated && (
            <p className="mt-4 text-sm text-[#334155]/70">Last updated: {lastUpdated}</p>
          )}
        </div>
      </section>
      <section className="bg-white py-16 md:py-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto prose-legal text-[#334155]/90 leading-relaxed">
          {children}
        </div>
      </section>
    </div>
  );
}

const sectionTitleClass =
  "font-display text-xl md:text-2xl text-[#1E2761] tracking-tight mt-10 mb-3";

export function PrivacyPolicy() {
  return (
    <LegalLayout eyebrow="Legal" title="Privacy Policy" lastUpdated="February 2026">
      <p>
        Your privacy is important to NextChapter4You. This Privacy Policy
        explains how we collect, use and protect your personal information
        when you use our website and services. Please read this policy
        carefully.
      </p>

      <h2 className={sectionTitleClass}>1. Information We Collect</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <strong>Personal Information:</strong> We collect personal
          information that you provide to us, such as your name, email
          address and phone number for contacting you based on your enquiry.
        </li>
        <li>
          <strong>Usage Data:</strong> We may also collect information about
          how you access and use our website during your visit to enhance
          future support.
        </li>
      </ul>

      <h2 className={sectionTitleClass}>2. How We Use Your Information</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li>Provide and improve our services.</li>
        <li>Respond to your inquiries and provide support.</li>
        <li>Communicate with you about our services and updates.</li>
        <li>Analyze website usage and trends.</li>
        <li>Ensure the security of our website and services.</li>
      </ul>

      <h2 className={sectionTitleClass}>3. Data Retention</h2>
      <p>
        We retain your personal information only for as long as necessary to
        fulfill the purposes for which it was collected, including for the
        purposes of satisfying any legal or reporting requirements.
      </p>

      <h2 className={sectionTitleClass}>4. Data Security</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li>All personal information is transmitted using secure, encrypted HTTPS connections.</li>
        <li>Data is stored within Google Workspace, which complies with GDPR and uses strong technical and organizational safeguards.</li>
        <li>Only authorized personnel have access to your data.</li>
        <li>We implement appropriate measures to protect against unauthorized access, accidental loss, or disclosure.</li>
      </ul>

      <h2 className={sectionTitleClass}>5. Sharing Your Information</h2>
      <p>
        We do not sell, trade, or otherwise transfer your personal information
        to outside parties except when required to do so by law or in response
        to valid requests by public authorities.
      </p>

      <h2 className={sectionTitleClass}>6. Your Rights Under GDPR</h2>
      <p>
        If you are a resident of the European Economic Area (EEA), you have
        certain rights under the General Data Protection Regulation (GDPR),
        including:
      </p>
      <ul className="list-disc pl-5 space-y-2">
        <li><strong>Right to Access:</strong> Request access to the personal information we hold about you.</li>
        <li><strong>Right to Rectification:</strong> Request that we correct any inaccurate or incomplete personal information.</li>
        <li><strong>Right to Erasure:</strong> Request that we delete your personal information, under certain conditions.</li>
        <li><strong>Right to Restriction of Processing:</strong> Request that we restrict the processing of your personal information.</li>
        <li><strong>Right to Data Portability:</strong> Receive your personal information in a structured, commonly used and machine-readable format.</li>
        <li><strong>Right to Object:</strong> Object to the processing of your personal information.</li>
        <li><strong>Right to Withdraw Consent:</strong> Withdraw your consent at any time where applicable.</li>
      </ul>
      <p>
        We retain your personal data only for 12 months to respond to your
        inquiries and provide support. Once your data is no longer needed, it
        will be securely deleted. To exercise these rights, please contact us
        at{" "}
        <a className="text-[#1E2761] underline" href={`mailto:${SITE.email}`}>
          {SITE.email}
        </a>
        .
      </p>

      <h2 className={sectionTitleClass}>7. Cookies</h2>
      <p>
        Our website may use cookies to enhance your experience. You can
        choose to have your computer warn you each time a cookie is being
        sent, or you can choose to turn off all cookies through your browser
        settings. See our{" "}
        <a className="text-[#1E2761] underline" href="/cookie-policy">
          Cookie Policy
        </a>{" "}
        for details.
      </p>

      <h2 className={sectionTitleClass}>8. Third-Party Links</h2>
      <p>
        We use Google Workspace for storing and processing your data. Google
        Workspace complies with GDPR and implements strict technical and
        organizational safeguards to protect your information.
      </p>

      <h2 className={sectionTitleClass}>9. Children's Privacy</h2>
      <p>
        Our website and services are not directed to children under the age
        of 18 and we do not knowingly collect personal information from
        them.
      </p>

      <h2 className={sectionTitleClass}>10. Changes to This Privacy Policy</h2>
      <p>
        NextChapter4You reserves the right to modify this Privacy Policy at
        any time. We will post any changes on this page and the revised
        policy will be effective immediately upon posting.
      </p>

      <h2 className={sectionTitleClass}>11. Contact Information</h2>
      <p>
        If you have any questions about this Privacy Policy, please contact
        us at{" "}
        <a className="text-[#1E2761] underline" href={`mailto:${SITE.email}`}>
          {SITE.email}
        </a>
        .
      </p>
      <p className="mt-6 text-sm text-[#334155]/70">
        NextChapter4You is registered with the Dutch Chamber of Commerce (KvK)
        under number <strong>{SITE.kvk}</strong>, with its registered office in{" "}
        {SITE.registeredAddress}.
      </p>
    </LegalLayout>
  );
}

export function CookiePolicy() {
  return (
    <LegalLayout eyebrow="Legal" title="Cookie Policy" lastUpdated="February 2026">
      <p>
        This Cookie Policy explains how NextChapter4You uses cookies and
        similar technologies on our website. By continuing to use the site,
        you consent to the use of cookies as described below.
      </p>

      <h2 className={sectionTitleClass}>1. What Are Cookies?</h2>
      <p>
        Cookies are small text files placed on your device when you visit a
        website. They allow the site to recognise your browser and capture
        certain information to improve your experience.
      </p>

      <h2 className={sectionTitleClass}>2. Types of Cookies We Use</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <strong>Functional Cookies:</strong> Strictly necessary for the
          operation of the website and to deliver services you request.
        </li>
        <li>
          <strong>Preference Cookies:</strong> Remember choices you make so we
          can provide a more personalised experience.
        </li>
        <li>
          <strong>Statistics Cookies:</strong> Help us understand how
          visitors interact with the site by collecting anonymous information.
        </li>
        <li>
          <strong>Marketing Cookies:</strong> Used only with your consent to
          measure the effectiveness of campaigns.
        </li>
      </ul>

      <h2 className={sectionTitleClass}>3. Managing Cookies</h2>
      <p>
        Most browsers allow you to control cookies through their settings.
        You can refuse or delete cookies at any time. Disabling cookies may
        affect the functionality of parts of this website.
      </p>

      <h2 className={sectionTitleClass}>4. Third-Party Services</h2>
      <p>
        We may use third-party services such as Google Analytics that set
        their own cookies. These providers comply with GDPR and have their
        own privacy policies governing the use of cookies.
      </p>

      <h2 className={sectionTitleClass}>5. Updates</h2>
      <p>
        We may update this Cookie Policy from time to time. Any changes will
        be posted on this page with a revised effective date.
      </p>

      <h2 className={sectionTitleClass}>6. Contact</h2>
      <p>
        Questions about cookies? Reach us at{" "}
        <a className="text-[#1E2761] underline" href={`mailto:${SITE.email}`}>
          {SITE.email}
        </a>
        .
      </p>
    </LegalLayout>
  );
}

export function TermsConditions() {
  return (
    <LegalLayout eyebrow="Legal" title="Terms & Conditions" lastUpdated="February 2026">
      <p>
        These Terms and Conditions govern your use of the NextChapter4You
        website and any services offered through it. By accessing the site or
        engaging our services, you agree to be bound by these terms.
      </p>

      <h2 className={sectionTitleClass}>1. Services</h2>
      <p>
        NextChapter4You provides professional coaching and expat outplacement
        support. The scope, deliverables, fees and timelines of any
        engagement are agreed separately in a written proposal or service
        agreement.
      </p>

      <h2 className={sectionTitleClass}>2. Use of the Website</h2>
      <p>
        You agree to use the website only for lawful purposes and in a way
        that does not infringe the rights of, restrict, or inhibit anyone
        else's use of the site.
      </p>

      <h2 className={sectionTitleClass}>3. Intellectual Property</h2>
      <p>
        All content on this site, including text, images, logos, and
        graphics, is the property of NextChapter4You or its licensors and is
        protected by applicable intellectual property laws. You may not
        reproduce, distribute, or republish content without prior written
        consent.
      </p>

      <h2 className={sectionTitleClass}>4. Confidentiality</h2>
      <p>
        Information shared with us during a coaching, advisory, or
        outplacement engagement is treated as confidential and used only for
        the purposes of delivering the agreed services, subject to legal
        obligations.
      </p>

      <h2 className={sectionTitleClass}>5. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, NextChapter4You is not
        liable for any indirect, incidental, or consequential damages arising
        from your use of the website or our services. Our maximum aggregate
        liability for any direct loss is limited to the fees paid to us in
        the twelve months preceding the claim.
      </p>

      <h2 className={sectionTitleClass}>6. Third-Party Links</h2>
      <p>
        Our site may contain links to third-party websites. We are not
        responsible for the content, privacy practices, or terms of use of
        these third-party sites.
      </p>

      <h2 className={sectionTitleClass}>7. Governing Law</h2>
      <p>
        These Terms are governed by the laws of the Netherlands. Any
        disputes arising in connection with these Terms shall be subject to
        the exclusive jurisdiction of the courts of the Netherlands.
      </p>

      <h2 className={sectionTitleClass}>8. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. Continued use of the
        website after changes are posted constitutes acceptance of the
        revised Terms.
      </p>

      <h2 className={sectionTitleClass}>9. Contact</h2>
      <p>
        Questions about these Terms? Contact us at{" "}
        <a className="text-[#1E2761] underline" href={`mailto:${SITE.email}`}>
          {SITE.email}
        </a>
        .
      </p>
      <p className="mt-6 text-sm text-[#334155]/70">
        NextChapter4You is registered with the Dutch Chamber of Commerce (KvK)
        under number <strong>{SITE.kvk}</strong>, registered office:{" "}
        {SITE.registeredAddress}.
      </p>
    </LegalLayout>
  );
}
